import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { Toaster } from "sonner";

import { msalInstance } from "./auth/msalConfig";
import { AuthProvider, useAuthContext } from "./auth/AuthContext";
import { CurrentUserProvider, useCurrentUser } from "./auth/CurrentUserContext";
import SessionGuard from "./auth/SessionGuard";
import { Member360ApolloProvider } from "./lib/apollo/Member360ApolloProvider";
import { RoutePersister } from "./lib/RoutePersister";
import { useAstranaPortalScope } from "./lib/useAstranaPortalScope";
import LoadingSpinner from "./components/LoadingSpinner";
import LoginPage from "./pages/LoginPage";
import AdminLayout from "./admin/components/AdminLayout";
import UsersPage from "./admin/pages/UsersPage";
import PersonasPage from "./admin/pages/PersonasPage";
import FeatureFlagsPage from "./admin/pages/FeatureFlagsPage";
import AiTemplatesPage from "./admin/pages/AiTemplatesPage";
import UsagePage from "./admin/pages/UsagePage";
import AiSummaryPage from "./admin/pages/AiSummaryPage";
import VisitNotesPage from "./admin/pages/VisitNotesPage";
import ClinicalTemplatesPage from "./admin/pages/ClinicalTemplatesPage";

/**
 * Route guard for authentication and optional RBAC checks.
 *
 * Props (all optional beyond `children`):
 *  - `requiredRole`       — gate on useCurrentUser().hasRole(value)
 *  - `requiredPermission` — gate on useCurrentUser().hasPermission(value)
 *  - `requiredPersona`    — gate on useCurrentUser().currentPersona === value
 *
 * Auth-only routes (no new props) work exactly as before.
 * While the RBAC query is loading → renders the existing loading state (no redirect).
 * Authenticated but fails RBAC check → renders an inline "not authorized" message.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  requiredPersona?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  requiredPersona,
}: ProtectedRouteProps) {
  const auth = useAuthContext();
  const currentUser = useCurrentUser();

  if (auth.isLoading) return <LoadingSpinner />;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;

  // Only blank the screen on the INITIAL RBAC resolution (no role known yet).
  // A background refetch (e.g. a mutation that refetches connectCurrentUser, or a
  // persona switch) also flips `loading` true — but once we already have a resolved
  // role, returning the full-screen spinner here would unmount the whole route
  // subtree (incl. AdminLayout's nav + header), causing a jarring pop-out/in on
  // every admin action. Keep the chrome mounted and let the page body show its own
  // inline loading state instead.
  if (currentUser.loading && currentUser.role === null) return <LoadingSpinner />;

  // If connectCurrentUser errored AND this route carries any RBAC requirement,
  // block access. With errorPolicy:'all' the query can set error while user=null;
  // falling through to rbacFails would treat null persona as allow-all, contradicting
  // the CurrentUserContext docstring that says null → fail-closed for unknown state.
  const hasRbacRequirement =
    requiredRole !== undefined ||
    requiredPermission !== undefined ||
    requiredPersona !== undefined;

  if (currentUser.error && hasRbacRequirement) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <div
          role="alert"
          className="max-w-sm w-full rounded-xl border border-destructive/40 bg-destructive/10 px-6 py-5 text-center"
        >
          <p className="text-base font-semibold text-destructive m-0 mb-1">Access Unavailable</p>
          <p className="text-sm text-muted-foreground m-0 mb-4">
            Unable to verify your permissions. This is usually an expired session — sign out and back in to fix it.
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { void auth.logout(); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const rbacFails =
    (requiredRole !== undefined && !currentUser.hasRole(requiredRole)) ||
    (requiredPermission !== undefined && !currentUser.hasPermission(requiredPermission)) ||
    (requiredPersona !== undefined && currentUser.currentPersona !== requiredPersona);

  if (rbacFails) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <div
          role="alert"
          className="max-w-sm w-full rounded-xl border border-destructive/40 bg-destructive/10 px-6 py-5 text-center"
        >
          <p className="text-base font-semibold text-destructive m-0 mb-1">Not Authorized</p>
          <p className="text-sm text-muted-foreground m-0">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Standalone Admin Console app.
 *
 * Apollo lift: Member360ApolloProvider is mounted ONCE here, wrapping all
 * routes. This ensures a single Apollo client and cache is shared across route
 * transitions, and allows CurrentUserProvider (which issues connectCurrentUser)
 * to be mounted above the route tree.
 *
 * Member360ApolloProvider requires BrowserRouter (uses useNavigate), so it
 * lives inside BrowserRouter but outside Routes. CurrentUserProvider requires
 * the Apollo client, so it is nested inside Member360ApolloProvider.
 *
 * The admin surface (Users / Personas / Feature Flags) is the entire app: the
 * root path renders AdminLayout under a `requiredRole="admin"` gate. Admin ops
 * hit care-management-gql's CONFIG plane (member_360 schema) via the shared
 * Member360ApolloProvider — no separate connect-gql client.
 */
/**
 * The route table for the admin console. Extracted from AppRoutes so it can be
 * unit-tested under a MemoryRouter without the MSAL / Apollo / SessionGuard
 * provider stack (the auth hooks it depends on are mocked in the test).
 * Rendered inside a router by AppRoutes below.
 */
export function RouteTable() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/users" replace />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="personas" element={<PersonasPage />} />
        <Route path="feature-flags" element={<FeatureFlagsPage />} />
        <Route path="ai-templates" element={<AiTemplatesPage />} />
        <Route path="ai-summary" element={<AiSummaryPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="visit-notes" element={<VisitNotesPage />} />
        <Route
          path="clinical-templates"
          element={<ClinicalTemplatesPage />}
        />
      </Route>
      {/* Unknown paths → the admin root (which redirects to /users). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  // SessionGuard lives inside <BrowserRouter> so it can navigate to
  // /login on explicit sign-out, and inside <AuthProvider> (mounted
  // above by the App component) so it can read auth state.
  return (
    <BrowserRouter>
      <RoutePersister />
      <Member360ApolloProvider>
        <CurrentUserProvider>
          <SessionGuard>
            <RouteTable />
          </SessionGuard>
        </CurrentUserProvider>
      </Member360ApolloProvider>
    </BrowserRouter>
  );
}

export default function App() {
  // Scope @astrana/ui's body-portaled dialogs/popovers without classing <body>
  // (which would leak the lib's typography onto app chrome).
  useAstranaPortalScope();

  // Provider mount order matters: AuthProvider depends on MsalProvider being
  // above it in the tree. This standalone app is web-only (no extension iframe),
  // so there is no AuthBridgeProvider branch.
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <AppRoutes />
      </AuthProvider>
    </MsalProvider>
  );
}
